import React, { useState } from 'react';
import { withPrefix } from 'gatsby';
import styled from 'styled-components';
import { GlobalStyle, Page, Header } from './portfolio-shell';
import Head from '@components/head';

const Hero = styled.section`
  padding: 64px 0 58px;
  display: grid;
  grid-template-columns: 1fr 225px;
  gap: 60px;
  align-items: center;
  .hello {
    color: var(--accent);
    margin-bottom: 16px;
  }
  h1 {
    font-family: Charter, 'Bitstream Charter', Georgia, serif;
    font-size: clamp(42px, 5.4vw, 68px);
    font-weight: 400;
    line-height: 1.04;
    letter-spacing: -0.055em;
  }
  h1 em {
    font-weight: 400;
  }
  .intro {
    max-width: 550px;
    margin-top: 25px;
    color: var(--muted);
    font-size: 17px;
    line-height: 1.65;
  }
  figure {
    transform: rotate(4deg);
    background: #fff;
    padding: 9px 9px 13px;
    box-shadow: 0 4px 18px #17221912;
    color: #4b544d;
  }
  figure img {
    display: block;
    width: 100%;
    height: 166px;
    object-fit: cover;
  }
  figcaption {
    font-family: Charter, Georgia, serif;
    font-style: italic;
    font-size: 14px;
    padding-top: 10px;
    text-align: center;
  }
  @media (max-width: 800px) {
    gap: 32px;
    grid-template-columns: 1fr 170px;
    figure img {
      height: 145px;
    }
  }
  @media (max-width: 600px) {
    padding: 42px 0;
    display: block;
    h1 {
      font-size: 46px;
    }
    figure {
      display: none;
    }
    .intro {
      font-size: 16px;
    }
  }
`;

const SectionHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 20px;
  border-top: 1px solid var(--line);
  padding: 22px 0 24px;
  h2 {
    font-size: 14px;
    font-weight: 500;
  }
  span {
    color: var(--muted);
    font-family: Charter, Georgia, serif;
    font-style: italic;
    font-size: 15px;
  }
`;

const Feature = styled.article`
  background: #e4ebce;
  color: #263127;
  border-radius: 5px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 395px;
  overflow: hidden;
  .copy {
    padding: 38px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  .eyebrow {
    color: #526047;
  }
  h3 {
    font-family: Charter, Georgia, serif;
    font-weight: 400;
    font-size: 38px;
    letter-spacing: -0.04em;
    line-height: 1.1;
    margin: 17px 0;
    max-width: 370px;
  }
  p {
    font-size: 16px;
    color: #4c594a;
    max-width: 350px;
  }
  .metric {
    margin-top: 22px;
    font-size: 14px;
    color: #354330;
  }
  .metric strong {
    font-size: 23px;
    font-weight: 500;
    letter-spacing: -0.04em;
    padding-right: 5px;
  }
  button {
    margin-top: 22px;
    border: 0;
    border-radius: 3px;
    color: #f9fbf1;
    background: #2f412b;
    padding: 11px 17px;
    cursor: pointer;
    font-size: 14px;
  }
  button:hover {
    background: #435837;
  }
  .visual {
    align-self: stretch;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 30px 30px 30px 5px;
  }
  .visual img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 4px;
    box-shadow: 0 12px 32px #26312717;
    transition: transform 250ms ease;
  }
  .visual a:hover img {
    transform: translateY(-4px);
  }
  .visual small {
    font-size: 12px;
    color: #526047;
    margin-top: 12px;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    .copy {
      padding: 28px;
    }
    .visual {
      padding: 0 28px 28px;
    }
    h3 {
      font-size: 35px;
    }
  }
`;

const Video = styled.div`
  margin-top: 18px;
  padding: 14px;
  background: var(--ink);
  border-radius: 5px;
  iframe {
    width: 100%;
    aspect-ratio: 16 / 9;
    border: 0;
    display: block;
  }
`;

const Story = styled.details`
  border-bottom: 1px solid var(--line);
  margin: 0 0 44px;
  summary {
    cursor: pointer;
    padding: 18px 0;
    color: var(--muted);
    font-size: 14px;
  }
  summary:hover {
    color: var(--ink);
  }
  .story-copy {
    max-width: 680px;
    padding: 3px 0 24px;
  }
  p {
    margin-bottom: 12px;
    color: var(--muted);
  }
`;

const Project = styled.article`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
  padding: 0 0 32px;
  h3 {
    font-family: Charter, Georgia, serif;
    font-size: 37px;
    line-height: 1.12;
    letter-spacing: -0.04em;
    font-weight: 400;
    margin: 16px 0;
  }
  .description {
    color: var(--muted);
    margin-bottom: 24px;
  }
  .research-stage {
    background: #eee9e2;
    border-radius: 5px;
    padding: 35px;
    color: #454039;
  }
  .research-stage .flow {
    display: grid;
    gap: 12px;
    margin: 24px 0;
  }
  .flow div {
    border: 1px solid #c4bbae;
    padding: 13px 17px;
    font-family: Charter, Georgia, serif;
    font-size: 20px;
  }
  .flow span {
    padding-left: 17px;
    font-size: 18px;
  }
  .research-stage .footnote {
    font-size: 13px;
    color: #6e655a;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 26px;
    h3 {
      font-size: 32px;
    }
    .research-stage {
      padding: 27px;
    }
  }
`;

const About = styled.section`
  padding: 8px 0 62px;
  .about-grid {
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    gap: 70px;
    padding: 12px 0 0;
  }
  h3 {
    font-family: Charter, Georgia, serif;
    font-size: 33px;
    font-weight: 400;
    letter-spacing: -0.035em;
    line-height: 1.2;
    margin-bottom: 20px;
  }
  p {
    color: var(--muted);
    margin-bottom: 16px;
  }
  figure img {
    width: 100%;
    height: 210px;
    object-fit: cover;
    border-radius: 3px;
  }
  figcaption {
    font-size: 13px;
    color: var(--muted);
    margin-top: 10px;
  }
  .elsewhere {
    display: flex;
    gap: 22px;
    flex-wrap: wrap;
    margin-top: 25px;
  }
  @media (max-width: 700px) {
    .about-grid {
      grid-template-columns: 1fr;
      gap: 30px;
    }
  }
`;

const Footer = styled.footer`
  border-top: 1px solid var(--line);
  padding: 35px 0 25px;
  .contact {
    display: flex;
    justify-content: space-between;
    gap: 25px;
    align-items: center;
  }
  h2 {
    font-family: Charter, Georgia, serif;
    font-size: 34px;
    font-weight: 400;
    letter-spacing: -0.035em;
  }
  .endnote {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    margin-top: 50px;
    color: var(--muted);
    font-size: 12px;
  }
  @media (max-width: 600px) {
    .contact {
      display: block;
    }
    .contact a {
      margin-top: 15px;
    }
  }
`;

const Investment = styled(Feature)`
  background: #e1eaf1;
  color: #233749;
  .eyebrow,
  p {
    color: #485d6b;
  }
  h3 {
    max-width: 400px;
  }
  .text-link {
    margin-top: 25px;
  }
  .product-preview {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 30px;
    height: 405px;
    overflow: hidden;
  }
  .product-preview img {
    display: block;
    width: 185px;
    height: auto;
    border: 6px solid #fff;
    border-radius: 24px;
    box-shadow: 0 20px 45px #25415a20;
    transform: rotate(5deg);
  }
  @media (max-width: 700px) {
    .product-preview {
      height: 290px;
      padding-top: 8px;
    }
  }
`;

const MoreProjects = styled.div`
  margin-bottom: 50px;
  article {
    display: grid;
    grid-template-columns: 175px 1fr;
    gap: 24px;
    padding: 23px 0;
    border-bottom: 1px solid var(--line);
  }
  article .eyebrow {
    padding-top: 5px;
  }
  h3 {
    font-size: 19px;
    font-weight: 500;
    line-height: 1.35;
    margin-bottom: 7px;
  }
  h3 a:hover {
    color: var(--accent);
  }
  p {
    color: var(--muted);
    max-width: 650px;
  }
  .text-link {
    margin-top: 10px;
    font-size: 14px;
  }
  @media (max-width: 600px) {
    article {
      grid-template-columns: 1fr;
      gap: 8px;
    }
  }
`;

const Teaching = styled.section`
  padding-bottom: 60px;
  .lecture {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 50px;
    align-items: center;
    padding: 24px 0;
  }
  h3 {
    font-family: Charter, Georgia, serif;
    font-size: 40px;
    font-weight: 400;
    letter-spacing: -0.04em;
    margin: 10px 0 16px;
    line-height: 1.15;
  }
  p:not(.eyebrow) {
    color: var(--muted);
    max-width: 620px;
  }
  .lecture-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    cursor: pointer;
    background: transparent;
    border: 0;
    color: var(--ink);
    font-size: 15px;
    min-width: 170px;
    padding: 20px;
  }
  .lecture-button span {
    display: grid;
    place-items: center;
    background: #e4ebce;
    color: #2f412b;
    border-radius: 50%;
    width: 68px;
    height: 68px;
    font-size: 23px;
    transition: transform 160ms ease;
  }
  .lecture-button:hover span {
    transform: scale(1.06);
  }
  @media (max-width: 600px) {
    .lecture {
      grid-template-columns: 1fr;
      gap: 15px;
    }
    .lecture-button {
      flex-direction: row;
      justify-self: start;
      padding: 5px 0;
    }
  }
`;

const PortfolioPage = () => {
  const [dark, setDark] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [showLecture, setShowLecture] = useState(false);

  return (
    <>
      <GlobalStyle />
      <Head
        title="Software Engineer"
        canonicalPath="/"
        description="Shikhar Agnihotri’s projects, experiments, and a few things away from the keyboard."
      />
      <Page $dark={dark}>
        <a className="skip" href="#main">
          Skip to content
        </a>
        <div className="wrap">
          <Header>
            <a className="identity" href="#main">
              <img src={withPrefix('/photos/profile.webp')} width="96" height="96" alt="" />
              <span>Shikhar Agnihotri</span>
            </a>
            <nav aria-label="Main navigation">
              <a href="#work">Things I build</a>
              <a href="#teaching">Teaching</a>
              <a href="#about">About</a>
              <button
                type="button"
                onClick={() => setDark(!dark)}
                aria-label={dark ? 'Use light mode' : 'Use dark mode'}
                aria-pressed={dark}>
                <span aria-hidden="true">{dark ? '☀' : '☾'}</span>
              </button>
            </nav>
          </Header>
          <main id="main">
            <Hero>
              <div>
                <p className="eyebrow hello">Hey, I’m Shikhar.</p>
                <h1>
                  Serious systems.
                  <br />
                  <em>Occasionally silly ideas.</em>
                </h1>
                <p className="intro">
                  I’m a software engineer at Salesforce, based in Bellevue. Outside work, I follow
                  my curiosity into AI, browser experiments, and the occasional mountain trail.
                </p>
              </div>
              <figure>
                <div className="cascades-frame">
                  <img
                    src={withPrefix('/photos/cascades.webp')}
                    alt="Clouds rolling across the North Cascades"
                  />
                </div>
                <figcaption>North Cascades.</figcaption>
              </figure>
            </Hero>
            <section id="work" aria-labelledby="work-heading">
              <SectionHeading>
                <h2 id="work-heading">A few things I’ve put into the world</h2>
                <span>2020 — 2025</span>
              </SectionHeading>
              <Investment>
                <div className="copy">
                  <p className="eyebrow">01 / InvestInsights.ai · 2025</p>
                  <h3>
                    Research has enough tabs.
                    <br />I wanted a conversation.
                  </h3>
                  <p>
                    Filings, earnings calls, market news. I built InvestInsights to bring scattered
                    investment research into one place you can ask questions about.
                  </p>
                  <a
                    className="text-link"
                    href="https://investinsights.ai/"
                    target="_blank"
                    rel="noreferrer">
                    Explore InvestInsights ↗
                  </a>
                </div>
                <div className="product-preview">
                  <img
                    src={withPrefix('/projects/investinsights-chat.webp')}
                    alt="InvestInsights mobile app with a conversational entry point for investment research"
                    width="446"
                    height="900"
                  />
                </div>
              </Investment>
              <Story>
                <summary>What I built, and the part that’s hard</summary>
                <div className="story-copy">
                  <p>
                    I built the product end to end: data integrations, an MCP server, LLM workflows
                    for extracting structured signals, and the application experience.
                  </p>
                  <p>
                    The chat box is the easy part. Choosing useful context, grounding an answer in
                    sources, and making uncertainty clear are the problems that keep this
                    interesting.
                  </p>
                </div>
              </Story>
              <Project>
                <div
                  className="research-stage"
                  role="img"
                  aria-label="aoip.ai connects peer-to-peer voice communication, audio data generation, and cloud-scale experiments">
                  <p className="eyebrow">aoip.ai / Open-source SDK</p>
                  <div className="flow" aria-hidden="true">
                    <div>Peer-to-peer voice</div>
                    <span>↓</span>
                    <div>Audio data for AI</div>
                    <span>↓</span>
                    <div>Cloud-scale experiments</div>
                  </div>
                  <p className="footnote">Python · AWS · Slurm</p>
                </div>
                <div>
                  <p className="eyebrow muted">02 / aoip.ai · 2023</p>
                  <h3>
                    Better audio starts
                    <br />
                    with real-world conditions.
                  </h3>
                  <p className="description">
                    An open-source SDK bringing peer-to-peer voice communication and AI
                    experimentation together. We built tools for generating VoIP audio data and
                    running experiments at cloud scale.
                  </p>
                  <a
                    className="text-link"
                    href="https://github.com/KonanAI/aoip.ai"
                    target="_blank"
                    rel="noreferrer">
                    Explore the code ↗
                  </a>
                </div>
              </Project>
              <Story>
                <summary>What the SDK brings together</summary>
                <div className="story-copy">
                  <p>
                    Voice applications need more than a model. They need a way to move audio between
                    peers, produce useful data, and run experiments beyond a single machine. My work
                    on aoip.ai explored that infrastructure using Python, AWS, and Slurm.
                  </p>
                  <a
                    className="text-link"
                    href="https://drive.google.com/file/d/1NUPdqnY3f7Lc8ogv5nYA5F3v040mKHKt/view?usp=sharing"
                    target="_blank"
                    rel="noreferrer">
                    Project overview ↗
                  </a>
                </div>
              </Story>
              <Feature>
                <div className="copy">
                  <p className="eyebrow">03 / A browser experiment · 2020</p>
                  <h3>
                    What if you showed up
                    <br />
                    to a meeting as Batman?
                  </h3>
                  <p>
                    Google Meet Classic Impersonator turns your movements into animated characters.
                    A remote-meeting experiment that found an audience of its own.
                  </p>
                  <p className="metric">
                    <strong>220K+</strong> installs at its peak
                  </p>
                  <button
                    type="button"
                    aria-expanded={showDemo}
                    aria-controls="meet-demo"
                    onClick={() => setShowDemo(!showDemo)}>
                    {showDemo ? 'Close demo ×' : '▶  Watch it in action'}
                  </button>
                </div>
                <div className="visual">
                  <a
                    href="https://www.youtube.com/watch?v=RvUI0IsnkeE"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Watch the Google Meet Classic Impersonator demo on YouTube">
                    <img
                      src={withPrefix('/projects/meet-impersonator.webp')}
                      alt="Google Meet Classic Impersonator’s character selection shown in its Chrome Web Store listing"
                      width="1600"
                      height="873"
                    />
                  </a>
                  <small>Google Meet Classic Impersonator / Chrome Web Store</small>
                </div>
              </Feature>
              <div id="meet-demo">
                {showDemo && (
                  <Video>
                    <iframe
                      src="https://www.youtube-nocookie.com/embed/RvUI0IsnkeE"
                      title="Google Meet Classic Impersonator demo"
                      allow="encrypted-media; picture-in-picture"
                      allowFullScreen
                    />
                    <a
                      className="text-link"
                      style={{ color: 'var(--paper)' }}
                      href="https://www.youtube.com/watch?v=RvUI0IsnkeE"
                      target="_blank"
                      rel="noreferrer">
                      Open on YouTube ↗
                    </a>
                  </Video>
                )}
              </div>
              <Story>
                <summary>The story behind the experiment</summary>
                <div className="story-copy">
                  <p>
                    During the shift to remote meetings, I wanted to replace live video with
                    animated characters. The extension ran pose estimation in the browser and
                    translated movement into SVG animation.
                  </p>
                  <p>
                    The interesting constraint: all that machine learning had to fit inside a
                    browser extension and keep up with a live conversation. At its peak, the project
                    reached 220K+ installs and 27K+ daily active users.
                  </p>
                  <a
                    className="text-link"
                    href="https://chromewebstore.google.com/detail/google-meet-classic-imper/eojkkpaoohebkdkplhjjcedcpkajlibe"
                    target="_blank"
                    rel="noreferrer">
                    Chrome Web Store ↗
                  </a>
                </div>
              </Story>
              <MoreProjects aria-labelledby="more-heading">
                <SectionHeading>
                  <h2 id="more-heading">More experiments</h2>
                  <span>A few other rabbit holes</span>
                </SectionHeading>
                <article>
                  <span className="eyebrow muted">Optimization</span>
                  <div>
                    <h3>
                      <a
                        href="https://shikhar-scs.github.io/bayesian-retails/"
                        target="_blank"
                        rel="noreferrer">
                        Walket ↗
                      </a>
                    </h3>
                    <p>
                      Batching orders and finding routes through a warehouse, using heuristics and
                      genetic algorithms.
                    </p>
                  </div>
                </article>
                <article>
                  <span className="eyebrow muted">Search</span>
                  <div>
                    <h3>Building a search engine</h3>
                    <p>
                      A CMU project exploring how ranking changes with BM25, learning to rank, and
                      dense retrieval.
                    </p>
                  </div>
                </article>
                <article>
                  <span className="eyebrow muted">Cloud systems</span>
                  <div>
                    <h3>A terabyte of tweets</h3>
                    <p>
                      From raw data to a queryable service: ETL, schema design, and cloud deployment
                      for a large tweet corpus.
                    </p>
                  </div>
                </article>
                <article>
                  <span className="eyebrow muted">Speech + ML</span>
                  <div>
                    <h3>Speech enhancement over networks</h3>
                    <p>
                      My CMU capstone on speech affected by noise and network degradation, evaluated
                      for quality and intelligibility.
                    </p>
                    <a
                      className="text-link"
                      href="https://arxiv.org/pdf/2303.09048.pdf"
                      target="_blank"
                      rel="noreferrer">
                      Related paper ↗
                    </a>
                  </div>
                </article>
              </MoreProjects>
            </section>
            <Teaching id="teaching" aria-labelledby="teaching-heading">
              <SectionHeading>
                <h2 id="teaching-heading">At the whiteboard</h2>
                <span>Teaching at Carnegie Mellon</span>
              </SectionHeading>
              <div className="lecture">
                <div>
                  <p className="eyebrow muted">Deep Learning / TA lecture</p>
                  <h3>Transformers &amp; LLMs</h3>
                  <p>
                    A lecture I gave while teaching Deep Learning at CMU. A different side of the
                    work: getting the ideas out of code and explaining them to a room.
                  </p>
                </div>
                <button
                  type="button"
                  className="lecture-button"
                  aria-expanded={showLecture}
                  aria-controls="lecture-video"
                  onClick={() => setShowLecture(!showLecture)}>
                  <span aria-hidden="true">{showLecture ? '×' : '▶'}</span>
                  {showLecture ? 'Close lecture' : 'Watch the lecture'}
                </button>
              </div>
              <div id="lecture-video">
                {showLecture && (
                  <Video>
                    <iframe
                      src="https://www.youtube-nocookie.com/embed/obM_n_EeXd4"
                      title="Shikhar Agnihotri’s CMU lecture on Transformers and LLMs"
                      allow="encrypted-media; picture-in-picture"
                      allowFullScreen
                    />
                    <a
                      className="text-link"
                      style={{ color: 'var(--paper)' }}
                      href="https://www.youtube.com/watch?v=obM_n_EeXd4"
                      target="_blank"
                      rel="noreferrer">
                      Open on YouTube ↗
                    </a>
                  </Video>
                )}
              </div>
            </Teaching>
            <About id="about">
              <SectionHeading>
                <h2>A little about me</h2>
                <span>Beyond the project links</span>
              </SectionHeading>
              <div className="about-grid">
                <div>
                  <h3>
                    I like seeing an idea
                    <br />
                    become something people use.
                  </h3>
                  <p>
                    At work, I build backend systems and tools that help people troubleshoot
                    problems themselves. I especially enjoy taking a complicated workflow and making
                    it easier to understand and use.
                  </p>
                  <p>
                    At Carnegie Mellon, I also taught PyTorch as a Deep Learning TA to 200+ students
                    across two semesters. Building something and helping someone understand it both
                    make me work through the details.
                  </p>
                  <div className="elsewhere">
                    <a
                      className="text-link"
                      href="https://github.com/shikhar-scs"
                      target="_blank"
                      rel="noreferrer">
                      GitHub ↗
                    </a>
                    <a
                      className="text-link"
                      href="https://www.linkedin.com/in/shikhar9a/"
                      target="_blank"
                      rel="noreferrer">
                      LinkedIn ↗
                    </a>
                    <a className="text-link" href={withPrefix('/ShikharAgnihotri.pdf')}>
                      Résumé ↗
                    </a>
                  </div>
                </div>
                <div className="landscapes">
                  <figure>
                    <div className="cascades-frame">
                      <img
                        src={withPrefix('/photos/cascades.webp')}
                        alt="The North Cascades from Sahale Glacier Camp"
                        width="1920"
                        height="1280"
                        loading="lazy"
                      />
                    </div>
                    <figcaption>North Cascades, from Sahale Glacier Camp.</figcaption>
                  </figure>
                  <figure>
                    <img
                      src={withPrefix('/photos/denali.webp')}
                      loading="lazy"
                      alt="Denali rising above the clouds"
                      width="1920"
                      height="1080"
                    />
                    <figcaption>Denali, above the clouds.</figcaption>
                  </figure>
                </div>
              </div>
            </About>
          </main>
          <Footer id="contact">
            <div className="contact">
              <h2>Got something interesting?</h2>
              <a className="text-link" href="mailto:shikhar9a@gmail.com">
                Say hello <span aria-hidden="true">↗</span>
              </a>
            </div>
            <div className="endnote">
              <span>Shikhar Agnihotri · Bellevue, WA</span>
              <a href="#main">Back to top ↑</a>
            </div>
          </Footer>
        </div>
      </Page>
    </>
  );
};
export default PortfolioPage;
